/**
 * 打车账号绑定 —— `/api/ride-credential`（M2.5，2026-09-18）。
 *
 * 沿用 `http.ts` 那一套（`x-trace-id`、401 单飞刷新都在里面），本层只拼路径与 schema。
 *
 * ★ **明文纪律（比 D-1 出行人还紧）**：`bind` 的 `token` 是明文，且这一段拿着
 *   **能下真单、能看该账号的全部行程**。所以本层：
 *   **不缓存、不重试留痕、不打日志** —— 连"请求失败时把 body 打出来看看"都不许加。
 *   ⚠ 调用方也别把它放进 zustand / react-query 的缓存里（那会进 devtools，也可能被序列化）。
 *
 * ⚠ 三个端点都要登录（身份只从令牌取），所以用默认 `auth: 'required'`。
 */
import {
  bindRideCredentialRequestSchema,
  rideCredentialStatusSchema,
  type RideCredential,
} from '@cione/contracts'
import type { HttpClient } from './http'

export interface RideCredentialApi {
  /** 查当前绑定状态。做「账号设置」页时用；主流程（卡片）不需要先查。 */
  get(): Promise<RideCredential>
  /**
   * 绑定。`token` 是用户**粘贴的原文** —— 原样发，别 trim、别截断。
   *
   * ⚠ 失败的业务码只有两个值得单独处置：
   *   `IDENTITY.CREDENTIAL_FORMAT_INVALID`（空/过长/含不可见字符）→「格式不对，请重新复制一次」；
   *   `AUTH.LOGIN_REQUIRED` → 走现有登录流程。
   * ⚠⚠ 成功**不代表 Token 可用**：后端录入时只校验格式，真伪要到第一次实际叫车时
   *   才由供应商判定（"懒发现"）。文案别说「已验证」。
   */
  bind(token: string): Promise<RideCredential>
  /**
   * 解绑。⚠ **幂等**：没绑过时也回成功（`status` 为 `UNBOUND`），**不是 404** ——
   * 界面别按「404 = 出错了」写，那会让一次无害的重复点击弹出一个红色报错。
   */
  unbind(): Promise<RideCredential>
}

/** ⚠ 单数，没有 `s`。写成 `/api/ride-credentials` 会 404，而那时错的是路径不是代码。 */
const PATH = '/api/ride-credential'

export function createRideCredentialApi(http: HttpClient): RideCredentialApi {
  return {
    get() {
      return http.request({
        method: 'GET',
        path: PATH,
        schema: rideCredentialStatusSchema,
      })
    },

    bind(token) {
      return http.request({
        method: 'POST',
        path: PATH,
        /*
          ⚠⚠ 过一遍 strict schema 再发：它守的是「请求体里不许出现 userId」——
            后端只认令牌里的身份，多带一个字段今天会被忽略，但那是**今天**的行为。
          ⚠ 不 trim：后端要靠换行等不可见字符判格式错，替它擦干净等于把那条校验废掉。
        */
        body: bindRideCredentialRequestSchema.parse({ token }),
        schema: rideCredentialStatusSchema,
      })
    },

    unbind() {
      return http.request({
        method: 'DELETE',
        path: PATH,
        schema: rideCredentialStatusSchema,
      })
    },
  }
}
